import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { codeFormatter } from 'utils';
import {
  getTableV2DetailBodyCellSx,
  getTableV2HeaderCellSx,
  tableV2DetailPanelTitleSx,
  tableV2DetailPanelWrapperSx,
  tableV2EmptyStateSx,
  tableV2TableSx,
} from '../../../components/v2/tableV2Styles';

/** Linhas compactas no painel de detalhe (sub-tabela) */
const DETAIL_ROW_HEIGHT = 44;

const formatDate = (value) =>
  value ? moment(value).format('DD/MM/YYYY HH:mm') : '—';

const buildDetailTitle = (produtoNome, produtoCodigo) => {
  if (!produtoNome && produtoCodigo == null) {
    return 'Detalhe por tamanho e local';
  }
  const codigo =
    produtoCodigo != null && produtoCodigo !== ''
      ? ` (${codeFormatter(produtoCodigo)})`
      : '';
  return `Detalhe · ${produtoNome || 'Produto'}${codigo}`;
};

const EstoqueVariantesDetail = ({
  variantes,
  localFilter,
  produtoNome,
  produtoCodigo,
}) => {
  const headerCellSx = getTableV2HeaderCellSx(DETAIL_ROW_HEIGHT);
  const title = buildDetailTitle(produtoNome, produtoCodigo);

  if (!variantes || variantes.length === 0) {
    return (
      <Box sx={tableV2DetailPanelWrapperSx}>
        <Typography component="div" sx={tableV2DetailPanelTitleSx}>
          {title}
        </Typography>
        <Box sx={{ ...tableV2EmptyStateSx, py: 2, px: 2 }}>
          Nenhuma variante encontrada.
        </Box>
      </Box>
    );
  }

  const highlightSx = {
    backgroundColor: (t) =>
      t.palette.mode === 'dark'
        ? `${t.palette.primary.main}1F`
        : `${t.palette.primary.main}12`,
    borderLeft: (t) => `3px solid ${t.palette.primary.main}`,
  };

  return (
    <Box sx={tableV2DetailPanelWrapperSx}>
      <Typography component="div" sx={tableV2DetailPanelTitleSx}>
        {title}
      </Typography>
      <Box sx={{ overflowX: 'auto', px: { xs: 0.5, sm: 1 }, pb: 0.5 }}>
        <Table
          size="medium"
          aria-label="Detalhe de estoque por tamanho e local"
          sx={tableV2TableSx}
        >
          <TableHead>
            <TableRow hover={false}>
              <TableCell sx={headerCellSx}>Tamanho</TableCell>
              <TableCell sx={headerCellSx}>Local</TableCell>
              <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>
                Quantidade
              </TableCell>
              <TableCell sx={headerCellSx}>Data última entrada</TableCell>
              <TableCell sx={headerCellSx}>Data última saída</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {variantes.map((variante, rowIndex) => {
              const isHighlighted =
                variante.localFiltrado === true ||
                (localFilter && variante.local === localFilter);
              const baseCellSx = getTableV2DetailBodyCellSx(
                DETAIL_ROW_HEIGHT,
                rowIndex
              );
              const cellSx = isHighlighted
                ? { ...baseCellSx, backgroundColor: 'transparent' }
                : baseCellSx;

              return (
                <TableRow
                  key={variante.id ?? `${variante.local}-${variante.produtoTamanhoNome}`}
                  hover={!isHighlighted}
                  sx={isHighlighted ? highlightSx : undefined}
                >
                  <TableCell sx={cellSx}>
                    {variante.produtoTamanhoNome || '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      fontWeight: isHighlighted ? 600 : 400,
                    }}
                  >
                    {variante.local}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, textAlign: 'right' }}>
                    {variante.quantidade}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {formatDate(variante.dataUltimaEntrada)}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {formatDate(variante.dataUltimaSaida)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

EstoqueVariantesDetail.propTypes = {
  variantes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      produtoTamanhoNome: PropTypes.string,
      local: PropTypes.string,
      quantidade: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      dataUltimaEntrada: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      dataUltimaSaida: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      localFiltrado: PropTypes.bool,
    })
  ).isRequired,
  localFilter: PropTypes.string,
  produtoNome: PropTypes.string,
  produtoCodigo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

EstoqueVariantesDetail.defaultProps = {
  localFilter: undefined,
  produtoNome: undefined,
  produtoCodigo: undefined,
};

export default EstoqueVariantesDetail;
